import React from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  ButtonGroup,
  Typography,
  makeStyles,
  TableCell,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Switch,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {
  QuoteResponse,
  ModuleDiscount,
  QuoteResponseLineItem,
  PagedQuoteHistory,
  QuoteDetailItem,
} from 'lib/Model/QuoteGenerator';
import {
  generateQuote,
  determineTierSize,
  fetchQuoteHistory,
  fetchQuoteDetails,
  updateQuoteCompany,
} from 'lib/Api/QuoteGenerator';
import pricingData from 'lib/Api/data/pricing.json';
import pricingDataOld from 'lib/Api/data/pricing_old.json';
import { Alert, Autocomplete } from '@material-ui/lab';
import { fetchDriveCompanies } from 'lib/Api/Drive';
import { DriveCompany } from 'lib/Model/Drive';
import {
  getTableSettingsStorageKey,
  TableSettings,
  useDebounce,
  useLocalStorageSettings,
} from 'hooks';
import { useLocation } from 'react-router-dom';
import QuoteDetailsDialog from './components/QuoteDetailsDialog';
import { Warning } from '@material-ui/icons';
import QuoteGenerationDialog from './components/QuoteGenerationDialog';
import { AllocationCompany } from 'lib/Model/ClientAllocation';
import QuoteHistoryTable from './components/QuoteHistoryTable';
import PricingTableDialog from './components/PricingTableDialog';

// better just in case, to be a bit flexible with pricing.json
const CORE_MODULES = [
  { key: 'hr_essentials_leave', label: 'HR Essentials & Leave' },
  { key: 'payroll', label: 'Payroll Calculations' },
  { key: 'performance', label: 'Performance' },
  { key: 'time_and_attendance', label: 'Time & Attendance' },
  { key: 'training_management', label: 'Training Management' },
  // { key: 'csp_per_payslip', label: 'CSP Payslips' },
];

const CORE_HARDWARE = [
  { key: 'speedface', label: 'SpeedFace V3L' },
  { key: 'horus', label: 'Horus E2' },
  {
    key: 'shared_team_virtual_punchclock',
    label: 'Shared Team Virtual Punchclock',
  },
  { key: 'rfid_em_cards', label: 'RFID EM Cards' },
];

const INSTALLATION_DEVICE_KEYS = new Set(['speedface', 'horus']);

const CORE_EXTRAS = [
  { key: 'data_migration', label: 'Data Migration' },
  { key: 'parallel_payroll_runs', label: 'Parallel Payroll Runs' },
  { key: 'signrequest', label: 'SignRequest' },
  { key: 'reporting_database', label: 'Reporting Database' },
  { key: 'onboarding_session', label: 'Onboarding Session' },
  { key: 'visitor_management', label: 'Visitor Management System' },
];

const CORE_ATS = [
  { key: 'admin_licence', label: 'HR Manager License' },
  { key: 'line_manager_licence', label: 'Hiring Manager License' },
];

const useStyles = makeStyles((theme) => ({
  stickyQuote: {
    position: 'sticky',
    top: theme.spacing(2),
    maxHeight: `calc(100vh - ${theme.spacing(4)}px)`,
    overflowY: 'auto',
    zIndex: 1,
  },
  stickyContainer: {
    position: 'relative',
    marginBottom: theme.spacing(3),
  },
  quoteHistorySection: {
    position: 'relative',
    zIndex: 2,
  },
  table: {
    minWidth: 650,
  },
  discountText: {
    color: theme.palette.error.main,
  },
  increaseText: {
    color: theme.palette.success.main,
  },
  quoteHeaderActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  saveButton: {
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    fontSize: '1.1rem',
  },
  sectionDivider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  moduleRow: {
    marginBottom: theme.spacing(1),
  },
  moduleControls: {
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1.5),
    borderLeft: `2px solid ${theme.palette.divider}`,
    '& .MuiOutlinedInput-input': {
      paddingTop: '8.5px',
      paddingBottom: '8.5px',
    },
  },
  hardwarePricingAlert: {
    marginBottom: theme.spacing(2),
  },
  hardwareOption: {
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  hardwareOptionLabel: {
    fontWeight: 500,
  },
  hardwarePricingText: {
    display: 'block',
    marginLeft: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  hardwareInstallationText: {
    display: 'block',
    marginLeft: theme.spacing(4),
    color: theme.palette.success.dark,
    fontWeight: 500,
  },
  hardwareNoInstallationText: {
    display: 'block',
    marginLeft: theme.spacing(4),
    color: theme.palette.warning.dark,
    fontWeight: 500,
  },
  mainContainer: {
    padding: theme.spacing(2),
  },
  contentSection: {
    marginBottom: theme.spacing(3),
  },
  folderFoundMessage: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.success.main,
    marginTop: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
  folderNotFoundMessage: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.primary,
    marginTop: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
  captionText: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    display: 'block',
  },
  summaryCard: {
    boxShadow: theme.shadows[3],
  },
  summaryMetrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  metricValue: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: theme.palette.primary.dark,
    marginLeft: theme.spacing(1),
  },
  metricLabel: {
    fontWeight: 500,
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  tableHeaderCell: {
    color: theme.palette.primary.contrastText,
    fontWeight: 'bold',
  },
  tablePaper: {
    border: `1px solid ${theme.palette.divider}`,
  },
  finalPriceCell: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    fontWeight: 'bold',
  },
  metricItem: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  finalPriceCellBody: {
    backgroundColor: theme.palette.background.default,
    fontWeight: 'bold',
  },
}));

const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCurrency = (num: number): string => `€${formatNumber(num)}`;

interface HardwarePricingInfo {
  unitPrice: number;
  installationFee: number;
}

export const QuoteGeneratorView: React.FC = () => {
  const classes = useStyles();
  const [companySize, setCompanySize] = React.useState<number>(0);
  const [companyName, setCompanyName] = React.useState<string>('');
  const [numberOfCompanies, setNumberOfCompanies] = React.useState<number>(1);
  const [requestedBy, setRequestedBy] = React.useState<string>('');
  const [selectedModules, setSelectedModules] = React.useState<string[]>([]);
  const [selectedHardware, setSelectedHardware] = React.useState<string[]>([]);
  const [selectedExtras, setSelectedExtras] = React.useState<string[]>([]);
  const [selectedATS, setSelectedATS] = React.useState<string[]>([]);
  const [quoteResult, setQuoteResult] = React.useState<QuoteResponse | null>(
    null,
  );
  const [quoteTimestamp, setQuoteTimestamp] = React.useState<Date | null>(null);
  const [moduleUserQty, setModuleUserQty] = React.useState<
    Record<string, number>
  >({});
  const [selectedHardwareQty, setSelectedHardwareQty] = React.useState<
    Record<string, number>
  >({});
  const [moduleDiscounts, setModuleDiscounts] = React.useState<
    Record<string, ModuleDiscount>
  >({});
  const [selectedATSQty, setSelectedATSQty] = React.useState<
    Record<string, number>
  >({});
  const [extrasQty, setExtrasQty] = React.useState<Record<string, number>>({});
  const [extrasDiscounts, setExtrasDiscounts] = React.useState<
    Record<string, { discountSetupFee: number; discountUnitPrice?: number }>
  >({});
  const [isQuoteStale, setIsQuoteStale] = React.useState<boolean>(false);
  const [selfInstall, setSelfInstall] = React.useState<boolean>(true);
  const [useOldPricing, setUseOldPricing] = React.useState<boolean>(false);
  const [proposalType, setProposalType] = React.useState<'regular' | 'csp'>(
    'regular',
  );
  const hasGeneratedQuote = React.useRef(false);

  const CSP_ALLOWED_MODULE_KEYS = React.useMemo(
    () => new Set(['payroll', 'time_and_attendance']),
    [],
  );

  const availableModules = React.useMemo(
    () =>
      proposalType === 'csp'
        ? CORE_MODULES.filter((m) => CSP_ALLOWED_MODULE_KEYS.has(m.key))
        : CORE_MODULES,
    [proposalType, CSP_ALLOWED_MODULE_KEYS],
  );

  const handleProposalTypeChange = (newType: 'regular' | 'csp') => {
    if (newType === proposalType) return;
    setProposalType(newType);
    if (newType === 'csp') {
      setSelectedModules((prev) =>
        prev.filter((key) => CSP_ALLOWED_MODULE_KEYS.has(key)),
      );
      setModuleUserQty((prev) => {
        const updated: Record<string, number> = {};
        Object.keys(prev).forEach((key) => {
          if (CSP_ALLOWED_MODULE_KEYS.has(key)) updated[key] = prev[key];
        });
        return updated;
      });
      setSelectedATS([]);
      setSelectedATSQty({});
      setSelectedHardware([]);
      setSelectedHardwareQty({});
      setSelectedExtras([]);
      setExtrasQty({});
      setExtrasDiscounts({});
      setSelfInstall(false);
      setModuleDiscounts((prev) => {
        const updated: Record<string, ModuleDiscount> = {};
        Object.keys(prev).forEach((key) => {
          if (CSP_ALLOWED_MODULE_KEYS.has(key)) updated[key] = prev[key];
        });
        return updated;
      });
    }
  };

  const [companies, setCompanies] = React.useState<
    DriveCompany[] | undefined
  >();
  const [loadingCompanies, setLoadingCompanies] = React.useState(false);
  const [selectedCompanyFromDropdown, setSelectedCompanyFromDropdown] =
    React.useState<DriveCompany | null>(null);

  const [quoteHistory, setQuoteHistory] = React.useState<
    PagedQuoteHistory | undefined
  >();
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  const [historyTextSearch, setHistoryTextSearch] = React.useState<
    string | undefined
  >();
  const debouncedHistoryTextSearch = useDebounce(historyTextSearch, 400);
  const [historyPageNumber, setHistoryPageNumber] = React.useState(0);
  const { pathname, hash } = useLocation();
  const [tableSettings, setTableSettings] =
    useLocalStorageSettings<TableSettings>(
      getTableSettingsStorageKey(pathname, hash),
      { rowsPerPage: 25 },
    );

  const [detailsDialogOpen, setDetailsDialogOpen] = React.useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = React.useState<number | null>(
    null,
  );
  const [quoteDetails, setQuoteDetails] = React.useState<QuoteDetailItem[]>([]);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const [detailsError, setDetailsError] = React.useState<string | null>(null);

  const [showHrWarningDialog, setShowHrWarningDialog] =
    React.useState<boolean>(false);
  const [pendingAction, setPendingAction] = React.useState<
    'generate' | 'finalize' | null
  >(null);

  const [quoteDataForGeneration, setQuoteDataForGeneration] =
    React.useState<QuoteResponse | null>(null);
  const [showGenerationDialog, setShowGenerationDialog] = React.useState(false);

  const [associationDialogOpen, setAssociationDialogOpen] =
    React.useState(false);
  const [selectedQuoteForAssociation, setSelectedQuoteForAssociation] =
    React.useState<{
      id: number;
      companyName: string;
    } | null>(null);

  const [selectedCompanyForAssociation, setSelectedCompanyForAssociation] =
    React.useState<AllocationCompany | null>(null);
  const [updatingCompany, setUpdatingCompany] = React.useState(false);
  const [updateError, setUpdateError] = React.useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = React.useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = React.useState(false);

  const [pricingDialogOpen, setPricingDialogOpen] = React.useState(false);

  const MIN_QTY_MODULE_KEYS = React.useMemo(
    () =>
      new Set([
        'hr_essentials_leave',
        'payroll',
        'performance',
        'training_management',
      ]),
    [],
  );

  const hardwarePricingByKey = React.useMemo(() => {
    const data: any = useOldPricing ? pricingDataOld : pricingData;
    const tierKey = determineTierSize(Math.max(companySize, 10), data.tiers);
    const addons = data.pricing[tierKey]?.addons ?? [];

    return CORE_HARDWARE.reduce<Record<string, HardwarePricingInfo>>(
      (pricing, hardware) => {
        const addon = addons.find((item: any) => item.module === hardware.key);
        const unitPrice = addon?.clocking_pro_price ?? addon?.price ?? 0;
        const installationFee = addon?.setup_price ?? 0;

        pricing[hardware.key] = {
          unitPrice,
          installationFee,
        };

        return pricing;
      },
      {},
    );
  }, [companySize, useOldPricing]);

  React.useEffect(() => {
    setModuleUserQty((prev) => {
      const updated = { ...prev };
      selectedModules.forEach((moduleKey) => {
        if (
          moduleKey !== 'csp_per_payslip' &&
          (moduleKey !== 'time_and_attendance' || !updated[moduleKey])
        ) {
          updated[moduleKey] = companySize;
        }
      });
      return updated;
    });
  }, [companySize, selectedModules, selectedATS]);

  React.useEffect(() => {
    if (hasGeneratedQuote.current) {
      setIsQuoteStale(true);
    }
  }, [
    companySize,
    companyName,
    selectedModules,
    selectedHardware,
    selectedExtras,
    selectedATS,
    moduleUserQty,
    selectedHardwareQty,
    moduleDiscounts,
    selectedATSQty,
    extrasQty,
    extrasDiscounts,
    selfInstall,
    useOldPricing,
  ]);

  const fetchCompanies = React.useCallback(() => {
    setLoadingCompanies(true);
    fetchDriveCompanies().then((d) => {
      setCompanies(d.data);
      setLoadingCompanies(false);
    });
  }, []);

  React.useEffect(() => {
    if (!loadingCompanies && companies === undefined) {
      fetchCompanies();
    }
  }, [fetchCompanies, loadingCompanies, companies]);

  React.useEffect(() => {
    if (selectedCompanyFromDropdown) {
      setCompanyName(selectedCompanyFromDropdown.company_title);
    } else {
      setCompanyName('');
    }
  }, [selectedCompanyFromDropdown]);

  const handleModuleChange = (moduleKey: string) => {
    setSelectedModules((prev) => {
      const newModules = prev.includes(moduleKey)
        ? prev.filter((key) => key !== moduleKey)
        : [...prev, moduleKey];

      if (!newModules.includes(moduleKey)) {
        // we will remove module quantity and discount when unchecked
        setModuleUserQty((prev) => {
          const updated = { ...prev };
          delete updated[moduleKey];
          return updated;
        });
        setModuleDiscounts((prev) => {
          const updated = { ...prev };
          delete updated[moduleKey];
          return updated;
        });
      } else {
        // here we set default discount when checked
        setModuleDiscounts((prev) => ({
          ...prev,
          [moduleKey]: { discountUnitPrice: 0, discountSetupFee: 0 },
        }));
        if (moduleKey !== 'csp_per_payslip') {
          setModuleUserQty((prev) => ({
            ...prev,
            [moduleKey]: companySize,
          }));
        }
      }

      return newModules;
    });
  };

  const handleHardwareChange = (hardwareKey: string) => {
    setSelectedHardware((prev) => {
      const isSelected = prev.includes(hardwareKey);
      const updated = isSelected
        ? prev.filter((key) => key !== hardwareKey)
        : [...prev, hardwareKey];
      if (isSelected) {
        setModuleDiscounts((prev) => {
          const updatedDiscounts = { ...prev };
          delete updatedDiscounts[hardwareKey];
          return updatedDiscounts;
        });
      } else {
        // set default discount when hardware is checked
        setModuleDiscounts((prev) => ({
          ...prev,
          [hardwareKey]: { discountUnitPrice: 0, discountSetupFee: 0 },
        }));
      }
      return updated;
    });
    setSelectedHardwareQty((prev) => {
      const updated = { ...prev };
      if (updated[hardwareKey] !== undefined) {
        delete updated[hardwareKey];
      } else {
        updated[hardwareKey] = 1;
      }
      return updated;
    });
  };

  const handleExtrasChange = (extrasKey: string) => {
    setSelectedExtras((prev) => {
      const newExtras = prev.includes(extrasKey)
        ? prev.filter((key) => key !== extrasKey)
        : [...prev, extrasKey];

      if (!newExtras.includes(extrasKey)) {
        setExtrasQty((prev) => {
          const updated = { ...prev };
          delete updated[extrasKey];
          return updated;
        });
        setExtrasDiscounts((prev) => {
          const updated = { ...prev };
          delete updated[extrasKey];
          return updated;
        });
      } else {
        setExtrasDiscounts((prev) => ({
          ...prev,
          [extrasKey]: {
            discountSetupFee: 0,
            discountUnitPrice: 0,
          },
        }));
      }

      return newExtras;
    });
  };

  const handleATSChange = (atsKey: string) => {
    setSelectedATS((prev) => {
      const isSelected = prev.includes(atsKey);
      const updated = isSelected
        ? prev.filter((key) => key !== atsKey)
        : [...prev, atsKey];
      if (isSelected) {
        setSelectedATSQty((prevQty) => {
          const updatedQty = { ...prevQty };
          delete updatedQty[atsKey];
          return updatedQty;
        });
      } else {
        setSelectedATSQty((prevQty) => ({
          ...prevQty,
          [atsKey]: companySize,
        }));
      }
      return updated;
    });
  };

  const shouldShowHrEssentialsWarning = () => {
    if (proposalType === 'csp') return false;
    const hasOtherCoreModules = selectedModules.some(
      (module) => module !== 'hr_essentials_leave',
    );
    const hasHrEssentials = selectedModules.includes('hr_essentials_leave');

    return hasOtherCoreModules && !hasHrEssentials;
  };

  const handleGenerateQuoteWithCheck = () => {
    if (shouldShowHrEssentialsWarning()) {
      setPendingAction('generate');
      setShowHrWarningDialog(true);
    } else {
      handleGenerateQuote();
    }
  };

  const handleFinaliseQuoteWithCheck = async () => {
    if (shouldShowHrEssentialsWarning()) {
      setPendingAction('finalize');
      setShowHrWarningDialog(true);
    } else {
      handleFinaliseQuote();
    }
  };

  const handleGenerateQuote = () => {
    const moduleSelections = selectedModules.map((key) => ({
      moduleKey: key,
      userQty: moduleUserQty[key] || companySize,
      ...(moduleDiscounts[key] ? moduleDiscounts[key] : {}),
    }));
    const hardwareSelections = selectedHardware.map((key) => ({
      moduleKey: key,
      userQty: selectedHardwareQty[key] || 1,
      discountUnitPrice: moduleDiscounts[key]?.discountUnitPrice || 0,
    }));
    const extraSelections = selectedExtras.map((key) => ({
      moduleKey: key,
      userQty:
        key === 'signrequest' ||
        key === 'reporting_database' ||
        key === 'parallel_payroll_runs' ||
        key === 'onboarding_session' ||
        key === 'data_migration' ||
        key === 'visitor_management'
          ? extrasQty[key] || 0
          : undefined,
      discountSetupFee: extrasDiscounts[key]?.discountSetupFee || 0,
      discountUnitPrice: extrasDiscounts[key]?.discountUnitPrice || 0,
    }));
    const atsSelections = selectedATS.map((atsKey) => ({
      moduleKey: atsKey,
      userQty: selectedATSQty[atsKey] || companySize,
      discountUnitPrice: moduleDiscounts[atsKey]?.discountUnitPrice || 0,
      discountSetupFee: moduleDiscounts[atsKey]?.discountSetupFee || 0,
    }));
    const allSelections = [
      ...moduleSelections,
      ...hardwareSelections,
      ...extraSelections,
      ...atsSelections,
    ];

    if (!selectedCompanyFromDropdown) {
      return;
    }

    const quote = generateQuote({
      companyName: selectedCompanyFromDropdown.company_title,
      companySize,
      companyId: selectedCompanyFromDropdown.id,
      numberOfCompanies,
      requestedBy,
      modules: allSelections,
      selfInstall,
      useOldPricing,
      isCspProposal: proposalType === 'csp',
    });
    setQuoteResult(quote);
    setQuoteTimestamp(new Date());
    setIsQuoteStale(false);
    hasGeneratedQuote.current = true;
  };

  const clearEverything = () => {
    setCompanyName('');
    setCompanySize(0);
    setSelectedModules([]);
    setSelectedHardware([]);
    setSelectedExtras([]);
    setSelectedATS([]);
    setModuleUserQty({});
    setSelectedHardwareQty({});
    setQuoteResult(null);
    setQuoteTimestamp(null);
    setSelectedCompanyFromDropdown(null);
    setSelfInstall(true);
    hasGeneratedQuote.current = false;
  };

  const resetAllStates = () => {
    setCompanyName('');
    setCompanySize(0);
    setNumberOfCompanies(1);
    setRequestedBy('');
    setSelectedModules([]);
    setSelectedHardware([]);
    setSelectedExtras([]);
    setSelectedATS([]);
    setQuoteResult(null);
    setQuoteTimestamp(null);
    setModuleUserQty({});
    setSelectedHardwareQty({});
    setModuleDiscounts({});
    setSelectedATSQty({});
    setExtrasQty({});
    setExtrasDiscounts({});
    setIsQuoteStale(false);
    setSelectedCompanyFromDropdown(null);
    setSelfInstall(true);
    setUseOldPricing(false);
    setProposalType('regular');
    hasGeneratedQuote.current = false;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getModuleName = (key: string): string => {
    const module = CORE_MODULES.find((m) => m.key === key);
    if (module) return module.label;
    const hardware = CORE_HARDWARE.find((h) => h.key === key);
    if (hardware) return hardware.label;
    const extra = CORE_EXTRAS.find((e) => e.key === key);
    if (extra) return extra.label;
    const ats = CORE_ATS.find((a) => a.key === key);
    if (ats) return ats.label;
    return key;
  };

  const getHardwarePricingText = (hardwareKey: string): string => {
    const pricing = hardwarePricingByKey[hardwareKey];
    if (!pricing) return '';

    const unitPrice = formatCurrency(pricing.unitPrice);
    const installationFee = formatCurrency(pricing.installationFee);

    if (INSTALLATION_DEVICE_KEYS.has(hardwareKey)) {
      const installedTotal = formatCurrency(
        pricing.unitPrice + pricing.installationFee,
      );
      return `Device only: ${unitPrice}. Installed by Talexio: ${unitPrice} + ${installationFee} installation = ${installedTotal} per device.`;
    }

    if (pricing.installationFee > 0) {
      return `Price: ${unitPrice}. One-time setup: ${installationFee}.`;
    }

    return `Price: ${unitPrice}. No installation fee is added.`;
  };

  const getHardwareInstallationText = (hardwareKey: string): string | null => {
    if (!INSTALLATION_DEVICE_KEYS.has(hardwareKey)) {
      return null;
    }

    const pricing = hardwarePricingByKey[hardwareKey];
    if (!pricing) return null;

    if (selfInstall) {
      return `No Talexio installation selected: quote uses device-only price and removes the ${formatCurrency(
        pricing.installationFee,
      )} installation fee per device.`;
    }

    return `Installation by Talexio selected: quote adds ${formatCurrency(
      pricing.installationFee,
    )} installation per device.`;
  };

  const getHardwareQuantityHelperText = (hardwareKey: string): string => {
    if (!INSTALLATION_DEVICE_KEYS.has(hardwareKey)) return '';

    return selfInstall
      ? 'Quantity multiplies the device-only price.'
      : 'Quantity multiplies both the device price and the €70 Talexio installation fee.';
  };

  const getSetupFeeTotal = (item: QuoteResponseLineItem): number => {
    const originalSetupFee = item.originalSetupFee ?? 0;
    return item.itemType === 'hardware'
      ? originalSetupFee * item.userQty
      : originalSetupFee;
  };

  const getSetupFeeAdjustmentTotal = (item: QuoteResponseLineItem): number => {
    const discountSetupFee = item.discountSetupFee ?? 0;
    return item.itemType === 'hardware'
      ? discountSetupFee * item.userQty
      : discountSetupFee;
  };

  const getFinalSetupFeeTotal = (item: QuoteResponseLineItem): number => {
    if (item.setupFee !== undefined) {
      return item.setupFee;
    }

    return Math.max(
      getSetupFeeTotal(item) + getSetupFeeAdjustmentTotal(item),
      0,
    );
  };

  const handleFinaliseQuote = async () => {
    if (!quoteResult) return;

    setQuoteDataForGeneration(quoteResult);
    setShowGenerationDialog(true);
  };

  const fetchQuoteHistoryData = React.useCallback(() => {
    setLoadingHistory(true);
    fetchQuoteHistory(
      historyPageNumber + 1,
      tableSettings.rowsPerPage,
      debouncedHistoryTextSearch,
    )
      .then((d) => {
        setLoadingHistory(false);
        setQuoteHistory(d);
      })
      .catch((error) => {
        setLoadingHistory(false);
      });
  }, [
    historyPageNumber,
    tableSettings.rowsPerPage,
    debouncedHistoryTextSearch,
  ]);

  React.useEffect(() => {
    fetchQuoteHistoryData();
  }, [fetchQuoteHistoryData]);

  const handleViewDetails = async (
    quoteId: number,
    companyId: string | null,
  ) => {
    setSelectedQuoteId(quoteId);
    setDetailsDialogOpen(true);
    setDetailsLoading(true);
    setDetailsError(null);

    try {
      const data = await fetchQuoteDetails(quoteId);
      setQuoteDetails(data);
    } catch (error) {
      setDetailsError(
        `Failed to load proposal details: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleOpenAssociationDialog = (
    quoteId: number,
    companyName: string,
  ) => {
    setSelectedQuoteForAssociation({ id: quoteId, companyName });
    setAssociationDialogOpen(true);
    setUpdateSuccess(false);
    setUpdateError(null);
    setSelectedCompanyForAssociation(null);
  };

  const handleCloseAssociationDialog = () => {
    setAssociationDialogOpen(false);
    setSelectedQuoteForAssociation(null);
    setUpdateSuccess(false);
    setUpdateError(null);
  };

  const handleUpdateCompany = async () => {
    if (!selectedCompanyForAssociation || !selectedQuoteForAssociation) return;

    setUpdatingCompany(true);
    setUpdateError(null);
    setUpdateSuccess(false);

    try {
      await updateQuoteCompany(
        selectedQuoteForAssociation.id,
        selectedCompanyForAssociation.id,
        selectedCompanyForAssociation.company_title,
      );
      setUpdateSuccess(true);
      fetchQuoteHistoryData();
    } catch (error) {
      setUpdateError(
        `Failed to update proposal: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    } finally {
      setUpdatingCompany(false);
    }
  };

  const refreshQuoteHistory = () => {
    fetchQuoteHistoryData();
  };

  const handleCloseGenerationDialog = () => {
    setShowGenerationDialog(false);
    setQuoteDataForGeneration(null);
    fetchCompanies();
    resetAllStates();
  };

  const handleGenerationSuccess = (generatedQuoteId: number) => {
    fetchQuoteHistoryData();
    setShowSuccessSnackbar(true);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader
            title="Create Talexio Proposal"
            subheader="Generate proposals for clients."
            action={
              <Box display="flex" alignItems="center" gridGap={8}>
                <ButtonGroup color="primary" aria-label="proposal type">
                  <Button
                    variant={
                      proposalType === 'regular' ? 'contained' : 'outlined'
                    }
                    onClick={() => handleProposalTypeChange('regular')}
                  >
                    Regular
                  </Button>
                  <Button
                    variant={proposalType === 'csp' ? 'contained' : 'outlined'}
                    onClick={() => handleProposalTypeChange('csp')}
                  >
                    CSP
                  </Button>
                </ButtonGroup>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setPricingDialogOpen(true)}
                >
                  View Pricing
                </Button>
              </Box>
            }
          />
          <Divider />
          <CardContent className={classes.mainContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} className={classes.contentSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Company Information
                </Typography>
                <Divider className={classes.sectionDivider} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.captionText}
                    >
                      Select a company from Zendesk. This will allow you to
                      automatically create a deal for this proposal. Data from
                      Zendesk is refreshed every 15 minutes.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <>
                      <Autocomplete
                        getOptionLabel={(c) => c.company_title}
                        options={companies ?? []}
                        loading={loadingCompanies}
                        value={selectedCompanyFromDropdown}
                        onChange={(e, newValue) => {
                          setSelectedCompanyFromDropdown(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="Company Name"
                            fullWidth
                            autoFocus
                            placeholder="Start typing to search for a company"
                            InputLabelProps={{ shrink: true }}
                            required
                          />
                        )}
                      />
                      {selectedCompanyFromDropdown && (
                        <Typography
                          variant="caption"
                          className={
                            selectedCompanyFromDropdown.proposals_folder_id
                              ? classes.folderFoundMessage
                              : classes.folderNotFoundMessage
                          }
                        >
                          {selectedCompanyFromDropdown.proposals_folder_id ? (
                            <>
                              <CheckCircleIcon fontSize="small" />
                              Drive folder found. Proposal can be automatically
                              created in this folder.
                            </>
                          ) : (
                            <>
                              <Warning fontSize="small" />A Google Drive folder
                              does not exist for this client yet and will be
                              automatically created with the document.
                            </>
                          )}
                        </Typography>
                      )}
                    </>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={
                        proposalType === 'csp'
                          ? 'Number of Payslips'
                          : 'Company Size'
                      }
                      value={companySize || ''}
                      onChange={(e) =>
                        setCompanySize(parseInt(e.target.value, 10))
                      }
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Number of Companies"
                      value={numberOfCompanies}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        setNumberOfCompanies(value > 0 ? value : 1);
                      }}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="number"
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Requested By"
                      value={requestedBy}
                      onChange={(e) => setRequestedBy(e.target.value)}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      placeholder="Optional"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useOldPricing}
                          onChange={(e) => setUseOldPricing(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Use Old Pricing"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  For all price adjustments below: enter positive values to
                  increase the price, negative values to reduce it.
                </Alert>
              </Grid>

              <Grid item xs={12} className={classes.contentSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Modules
                </Typography>
                <Divider className={classes.sectionDivider} />

                <FormControl component="fieldset" fullWidth>
                  <FormGroup>
                    {availableModules.map((module) => (
                      <Box key={module.key} className={classes.moduleRow}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedModules.includes(module.key)}
                              onChange={() => handleModuleChange(module.key)}
                            />
                          }
                          label={module.label}
                        />
                        {selectedModules.includes(module.key) && (
                          <>
                            <Grid
                              container
                              spacing={2}
                              className={classes.moduleControls}
                            >
                              {(module.key === 'time_and_attendance' ||
                                module.key === 'csp_per_payslip') && (
                                <Grid item>
                                  <TextField
                                    label={
                                      module.key === 'csp_per_payslip'
                                        ? 'Number of Payslips'
                                        : 'Number of Users'
                                    }
                                    value={moduleUserQty[module.key] ?? ''}
                                    onChange={(e) =>
                                      setModuleUserQty((prev) => ({
                                        ...prev,
                                        [module.key]: parseInt(
                                          e.target.value,
                                          10,
                                        ),
                                      }))
                                    }
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    type="number"
                                  />
                                </Grid>
                              )}
                              <Grid item>
                                <TextField
                                  label="Unit Price Adjustment"
                                  type="number"
                                  value={
                                    moduleDiscounts[module.key]
                                      ?.discountUnitPrice ?? ''
                                  }
                                  onChange={(e) =>
                                    setModuleDiscounts((prev) => ({
                                      ...prev,
                                      [module.key]: {
                                        ...prev[module.key],
                                        discountUnitPrice:
                                          e.target.value === ''
                                            ? undefined
                                            : parseFloat(e.target.value),
                                        discountSetupFee:
                                          prev[module.key]?.discountSetupFee ??
                                          0,
                                      },
                                    }))
                                  }
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              <Grid item>
                                <TextField
                                  label="Setup Fee Adjustment"
                                  type="number"
                                  value={
                                    moduleDiscounts[module.key]
                                      ?.discountSetupFee ?? ''
                                  }
                                  onChange={(e) =>
                                    setModuleDiscounts((prev) => ({
                                      ...prev,
                                      [module.key]: {
                                        ...prev[module.key],
                                        discountSetupFee:
                                          e.target.value === ''
                                            ? undefined
                                            : parseFloat(e.target.value),
                                        discountUnitPrice:
                                          prev[module.key]?.discountUnitPrice ??
                                          0,
                                      },
                                    }))
                                  }
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            </Grid>
                            {companySize > 0 &&
                              companySize < 10 &&
                              MIN_QTY_MODULE_KEYS.has(module.key) &&
                              !(
                                proposalType === 'csp' &&
                                module.key === 'payroll'
                              ) && (
                                <Typography
                                  variant="caption"
                                  className={classes.captionText}
                                >
                                  Monthly fee calculated based on a minimum of
                                  10 employees.
                                </Typography>
                              )}
                            {proposalType === 'csp' &&
                              module.key === 'payroll' && (
                                <Typography
                                  variant="caption"
                                  className={classes.captionText}
                                >
                                  Priced using CSP Per Payslip rates.
                                </Typography>
                              )}
                          </>
                        )}
                      </Box>
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>

              {proposalType === 'regular' && (
                <Grid item xs={12} className={classes.contentSection}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    ATS
                  </Typography>
                  <Divider className={classes.sectionDivider} />

                  <FormControl component="fieldset" fullWidth>
                    <FormGroup>
                      {CORE_ATS.map((ats) => (
                        <Box key={ats.key} className={classes.moduleRow}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedATS.includes(ats.key)}
                                onChange={() => handleATSChange(ats.key)}
                              />
                            }
                            label={ats.label}
                          />
                          {selectedATS.includes(ats.key) && (
                            <Grid
                              container
                              spacing={2}
                              className={classes.moduleControls}
                            >
                              <Grid item>
                                <TextField
                                  label="Number of Users"
                                  value={selectedATSQty[ats.key] ?? ''}
                                  onChange={(e) =>
                                    setSelectedATSQty((prev) => ({
                                      ...prev,
                                      [ats.key]: parseInt(e.target.value, 10),
                                    }))
                                  }
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                  type="number"
                                />
                              </Grid>
                              <Grid item>
                                <TextField
                                  label="Unit Price Adjustment"
                                  type="number"
                                  value={
                                    moduleDiscounts[ats.key]
                                      ?.discountUnitPrice || 0
                                  }
                                  onChange={(e) =>
                                    setModuleDiscounts((prev) => ({
                                      ...prev,
                                      [ats.key]: {
                                        ...prev[ats.key],
                                        discountUnitPrice:
                                          parseFloat(e.target.value) || 0,
                                        discountSetupFee:
                                          ats.key === 'line_manager_licence'
                                            ? 0
                                            : prev[ats.key]?.discountSetupFee ||
                                              0,
                                      },
                                    }))
                                  }
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              {ats.key !== 'line_manager_licence' && (
                                <Grid item>
                                  <TextField
                                    label="Setup Fee Adjustment"
                                    type="number"
                                    value={
                                      moduleDiscounts[ats.key]
                                        ?.discountSetupFee || 0
                                    }
                                    onChange={(e) =>
                                      setModuleDiscounts((prev) => ({
                                        ...prev,
                                        [ats.key]: {
                                          ...prev[ats.key],
                                          discountSetupFee:
                                            parseFloat(e.target.value) || 0,
                                          discountUnitPrice:
                                            prev[ats.key]?.discountUnitPrice ||
                                            0,
                                        },
                                      }))
                                    }
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                              )}
                            </Grid>
                          )}
                        </Box>
                      ))}
                    </FormGroup>
                  </FormControl>
                </Grid>
              )}

              {proposalType === 'regular' && (
                <Grid item xs={12} className={classes.contentSection}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Hardware
                  </Typography>
                  <Divider className={classes.sectionDivider} />
                  <Alert
                    severity="info"
                    className={classes.hardwarePricingAlert}
                  >
                    Device price and installation are separate. Leave
                    Installation by Talexio checked when Talexio will install
                    the device; the quote adds €70 installation per Speedface or
                    Horus E2. Untick it only when the client will install the
                    device themselves.
                  </Alert>

                  <Box mb={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!selfInstall}
                          onChange={(e) => setSelfInstall(!e.target.checked)}
                        />
                      }
                      label="Installation by Talexio (adds €70 per Speedface or Horus E2 device)"
                    />
                  </Box>

                  <FormControl component="fieldset" fullWidth>
                    <FormGroup>
                      {CORE_HARDWARE.map((hardware) => {
                        const installationText = getHardwareInstallationText(
                          hardware.key,
                        );
                        return (
                          <Box
                            key={hardware.key}
                            className={`${classes.moduleRow} ${classes.hardwareOption}`}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedHardware.includes(
                                    hardware.key,
                                  )}
                                  onChange={() =>
                                    handleHardwareChange(hardware.key)
                                  }
                                />
                              }
                              label={
                                <Typography
                                  variant="body2"
                                  className={classes.hardwareOptionLabel}
                                >
                                  {hardware.label}
                                </Typography>
                              }
                            />
                            <Typography
                              variant="caption"
                              className={classes.hardwarePricingText}
                            >
                              {getHardwarePricingText(hardware.key)}
                            </Typography>
                            {installationText && (
                              <Typography
                                variant="caption"
                                className={
                                  selfInstall
                                    ? classes.hardwareNoInstallationText
                                    : classes.hardwareInstallationText
                                }
                              >
                                {installationText}
                              </Typography>
                            )}
                            {selectedHardware.includes(hardware.key) && (
                              <Grid
                                container
                                spacing={2}
                                className={classes.moduleControls}
                              >
                                <Grid item>
                                  <TextField
                                    label="Quantity"
                                    value={
                                      selectedHardwareQty[hardware.key] || 0
                                    }
                                    onChange={(e) =>
                                      setSelectedHardwareQty((prev) => ({
                                        ...prev,
                                        [hardware.key]: parseInt(
                                          e.target.value,
                                          10,
                                        ),
                                      }))
                                    }
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    type="number"
                                    helperText={getHardwareQuantityHelperText(
                                      hardware.key,
                                    )}
                                  />
                                </Grid>
                                <Grid item>
                                  <TextField
                                    label="Unit Price Adjustment"
                                    type="number"
                                    value={
                                      moduleDiscounts[hardware.key]
                                        ?.discountUnitPrice || 0
                                    }
                                    onChange={(e) =>
                                      setModuleDiscounts((prev) => ({
                                        ...prev,
                                        [hardware.key]: {
                                          discountUnitPrice:
                                            parseFloat(e.target.value) || 0,
                                          discountSetupFee: 0,
                                        },
                                      }))
                                    }
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                              </Grid>
                            )}
                          </Box>
                        );
                      })}
                    </FormGroup>
                  </FormControl>
                </Grid>
              )}

              {proposalType === 'regular' && (
                <Grid item xs={12} className={classes.contentSection}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Extras
                  </Typography>
                  <Divider className={classes.sectionDivider} />

                  <FormControl component="fieldset" fullWidth>
                    <FormGroup>
                      {CORE_EXTRAS.map((extra) => (
                        <Box key={extra.key} className={classes.moduleRow}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedExtras.includes(extra.key)}
                                onChange={() => handleExtrasChange(extra.key)}
                              />
                            }
                            label={extra.label}
                          />
                          {selectedExtras.includes(extra.key) && (
                            <Grid
                              container
                              spacing={2}
                              className={classes.moduleControls}
                            >
                              {(extra.key === 'signrequest' ||
                                extra.key === 'reporting_database' ||
                                extra.key === 'parallel_payroll_runs' ||
                                extra.key === 'data_migration' ||
                                extra.key === 'visitor_management' ||
                                extra.key === 'onboarding_session') && (
                                <Grid item>
                                  <TextField
                                    label={
                                      extra.key === 'reporting_database'
                                        ? 'Number of Databases'
                                        : extra.key === 'parallel_payroll_runs'
                                        ? 'Number of Runs'
                                        : extra.key === 'data_migration'
                                        ? 'Number of Migrations'
                                        : extra.key === 'visitor_management'
                                        ? 'Number of Systems'
                                        : extra.key === 'onboarding_session'
                                        ? 'Number of Sessions'
                                        : 'Number of Users'
                                    }
                                    value={extrasQty[extra.key] || ''}
                                    onChange={(e) =>
                                      setExtrasQty((prev) => ({
                                        ...prev,
                                        [extra.key]: parseInt(
                                          e.target.value,
                                          10,
                                        ),
                                      }))
                                    }
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    type="number"
                                  />
                                </Grid>
                              )}
                              {(extra.key === 'signrequest' ||
                                extra.key === 'reporting_database' ||
                                extra.key === 'visitor_management') && (
                                <Grid item>
                                  <TextField
                                    label="Unit Price Adjustment"
                                    type="number"
                                    value={
                                      extrasDiscounts[extra.key]
                                        ?.discountUnitPrice || 0
                                    }
                                    onChange={(e) =>
                                      setExtrasDiscounts((prev) => ({
                                        ...prev,
                                        [extra.key]: {
                                          ...prev[extra.key],
                                          discountUnitPrice:
                                            parseFloat(e.target.value) || 0,
                                          discountSetupFee:
                                            prev[extra.key]?.discountSetupFee ||
                                            0,
                                        },
                                      }))
                                    }
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                              )}
                              {(extra.key === 'data_migration' ||
                                extra.key === 'parallel_payroll_runs' ||
                                extra.key === 'onboarding_session') && (
                                <Grid item>
                                  <TextField
                                    label="Setup Fee Adjustment"
                                    type="number"
                                    value={
                                      extrasDiscounts[extra.key]
                                        ?.discountSetupFee || 0
                                    }
                                    onChange={(e) =>
                                      setExtrasDiscounts((prev) => ({
                                        ...prev,
                                        [extra.key]: {
                                          ...prev[extra.key],
                                          discountSetupFee:
                                            parseFloat(e.target.value) || 0,
                                        },
                                      }))
                                    }
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                              )}
                            </Grid>
                          )}
                        </Box>
                      ))}
                    </FormGroup>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider className={classes.sectionDivider} />
                <Box display="flex" alignItems="center" gridGap={16} mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateQuoteWithCheck}
                    disabled={
                      !selectedCompanyFromDropdown || // Ensures a company is selected
                      !companyName || // Redundant if selectedCompanyFromDropdown implies companyName
                      companySize <= 0 ||
                      numberOfCompanies <= 0 ||
                      (selectedModules.length === 0 &&
                        selectedATS.length === 0 &&
                        selectedHardware.length === 0 &&
                        selectedExtras.length === 0) ||
                      selectedATS.some(
                        (atsKey) =>
                          !selectedATSQty[atsKey] ||
                          selectedATSQty[atsKey] <= 0,
                      )
                    }
                  >
                    Preview Proposal
                  </Button>
                  <Button onClick={clearEverything} variant="outlined">
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {quoteResult && (
        <Grid item xs={12} md={6} className={classes.stickyContainer}>
          <div className={classes.stickyQuote}>
            <Card className={classes.summaryCard}>
              <CardHeader
                title="Proposal Summary"
                subheader={`Generated proposal for ${quoteResult.companyName}${
                  quoteTimestamp ? ` at ${formatTime(quoteTimestamp)}` : ''
                }${
                  quoteResult.numberOfCompanies &&
                  quoteResult.numberOfCompanies > 1
                    ? ` (${quoteResult.numberOfCompanies} companies)`
                    : ''
                }${
                  quoteResult.requestedBy
                    ? ` - Requested by ${quoteResult.requestedBy}`
                    : ''
                }`}
                action={
                  <Box className={classes.quoteHeaderActions}>
                    {isQuoteStale && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleGenerateQuote}
                        size="small"
                      >
                        Regenerate
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.saveButton}
                      onClick={handleFinaliseQuoteWithCheck}
                      disabled={isQuoteStale}
                    >
                      Proceed to Finalise
                    </Button>
                  </Box>
                }
              />
              <Divider />
              <CardContent>
                {isQuoteStale && (
                  <Box mb={2}>
                    <Alert severity="warning">
                      Changes detected. Use "Regenerate" to update the preview
                      before finalising.
                    </Alert>
                  </Box>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <div className={classes.summaryMetrics}>
                      <div className={classes.metricItem}>
                        <Typography className={classes.metricLabel}>
                          One-time Fee
                        </Typography>
                        <Typography className={classes.metricValue}>
                          €{formatNumber(quoteResult.oneTimeFee)}
                        </Typography>
                      </div>
                      <div className={classes.metricItem}>
                        <Typography className={classes.metricLabel}>
                          Unit Price
                        </Typography>
                        <Typography className={classes.metricValue}>
                          €{formatNumber(quoteResult.totalUnitPrice)}
                          {quoteResult.atsUnitPrice > 0 && (
                            <span>
                              {' '}
                              (+ €{formatNumber(quoteResult.atsUnitPrice)} ATS)
                            </span>
                          )}
                        </Typography>
                      </div>
                      <div className={classes.metricItem}>
                        <Typography className={classes.metricLabel}>
                          Monthly Fee
                        </Typography>
                        <Typography className={classes.metricValue}>
                          €{formatNumber(quoteResult.monthlyFee)}
                        </Typography>
                      </div>
                      <div className={classes.metricItem}>
                        <Typography className={classes.metricLabel}>
                          Annual Fee
                        </Typography>
                        <Typography className={classes.metricValue}>
                          €{formatNumber(quoteResult.annualFee)}
                        </Typography>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      style={{ fontWeight: 600 }}
                    >
                      Detailed Breakdown
                    </Typography>
                    <TableContainer
                      component={Paper}
                      className={classes.tablePaper}
                    >
                      <Table
                        className={classes.table}
                        aria-label="quote details"
                        size="small"
                      >
                        <TableHead className={classes.tableHeader}>
                          <TableRow>
                            <TableCell className={classes.tableHeaderCell}>
                              Module Name
                            </TableCell>
                            <TableCell
                              align="center"
                              className={classes.tableHeaderCell}
                            >
                              Quantity
                            </TableCell>
                            <TableCell
                              align="center"
                              className={classes.tableHeaderCell}
                            >
                              Original Unit Price
                            </TableCell>
                            <TableCell
                              align="center"
                              className={classes.tableHeaderCell}
                            >
                              Price Adjustment
                            </TableCell>
                            <TableCell
                              align="center"
                              className={classes.finalPriceCell}
                            >
                              Final Unit Price
                            </TableCell>
                            <TableCell
                              align="center"
                              className={classes.tableHeaderCell}
                            >
                              Original Setup / Installation Total
                            </TableCell>
                            <TableCell
                              align="center"
                              className={classes.tableHeaderCell}
                            >
                              Setup / Installation Adjustment
                            </TableCell>
                            <TableCell
                              align="center"
                              className={classes.finalPriceCell}
                            >
                              Final Setup / Installation Total
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[
                            ...quoteResult.modules,
                            ...quoteResult.hardware,
                            ...quoteResult.extras,
                          ].map((item: QuoteResponseLineItem) => (
                            <TableRow key={item.moduleKey}>
                              <TableCell component="th" scope="row">
                                {getModuleName(item.moduleKey)}
                                {quoteResult.isCspProposal &&
                                  item.moduleKey === 'payroll' && (
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      display="block"
                                    >
                                      CSP Per Payslip pricing
                                    </Typography>
                                  )}
                              </TableCell>
                              <TableCell align="center">
                                {item.userQty}
                              </TableCell>
                              <TableCell align="center">
                                {item.originalPrice
                                  ? formatNumber(item.originalPrice)
                                  : '-'}
                              </TableCell>
                              <TableCell
                                align="center"
                                className={
                                  (item.discountUnitPrice ?? 0) < 0
                                    ? classes.discountText
                                    : (item.discountUnitPrice ?? 0) > 0
                                    ? classes.increaseText
                                    : undefined
                                }
                              >
                                {item.discountUnitPrice
                                  ? `${
                                      item.discountUnitPrice < 0 ? '-' : '+'
                                    }${formatNumber(
                                      Math.abs(item.discountUnitPrice),
                                    )}`
                                  : '-'}
                              </TableCell>
                              <TableCell
                                align="center"
                                className={classes.finalPriceCellBody}
                              >
                                {formatNumber(
                                  (item.originalPrice ?? 0) +
                                    (item.discountUnitPrice ?? 0),
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {getSetupFeeTotal(item)
                                  ? formatNumber(getSetupFeeTotal(item))
                                  : '-'}
                              </TableCell>
                              <TableCell
                                align="center"
                                className={
                                  getSetupFeeAdjustmentTotal(item) < 0
                                    ? classes.discountText
                                    : getSetupFeeAdjustmentTotal(item) > 0
                                    ? classes.increaseText
                                    : undefined
                                }
                              >
                                {getSetupFeeAdjustmentTotal(item)
                                  ? `${
                                      getSetupFeeAdjustmentTotal(item) < 0
                                        ? '-'
                                        : '+'
                                    }${formatNumber(
                                      Math.abs(
                                        getSetupFeeAdjustmentTotal(item),
                                      ),
                                    )}`
                                  : '-'}
                              </TableCell>
                              <TableCell
                                align="center"
                                className={classes.finalPriceCellBody}
                              >
                                {formatNumber(getFinalSetupFeeTotal(item))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </div>
        </Grid>
      )}

      <Grid item xs={12} className={classes.quoteHistorySection}>
        <QuoteHistoryTable
          quoteHistory={quoteHistory}
          loadingHistory={loadingHistory}
          historyTextSearch={historyTextSearch}
          setHistoryTextSearch={setHistoryTextSearch}
          historyPageNumber={historyPageNumber}
          setHistoryPageNumber={setHistoryPageNumber}
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          onViewDetails={handleViewDetails}
          onAssociateCompany={handleOpenAssociationDialog}
        />
      </Grid>

      {selectedQuoteId && (
        <QuoteDetailsDialog
          open={detailsDialogOpen}
          quoteDetails={quoteDetails}
          loading={detailsLoading}
          error={detailsError}
          onClose={() => setDetailsDialogOpen(false)}
          quoteId={selectedQuoteId}
          companyId={
            quoteHistory?.data?.find((q) => q.quote_id === selectedQuoteId)
              ?.company_id ?? null
          }
          companies={companies}
          loadingCompanies={loadingCompanies}
          onUpdateSuccess={refreshQuoteHistory}
          quoteSlideId={
            quoteHistory?.data?.find((q) => q.quote_id === selectedQuoteId)
              ?.quote_slide_id
          }
        />
      )}

      <Dialog
        open={showHrWarningDialog}
        onClose={() => setShowHrWarningDialog(false)}
        aria-labelledby="hr-warning-dialog-title"
      >
        <DialogTitle id="hr-warning-dialog-title">
          Module Recommendation
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            It's recommended to select the 'HR Essentials & Leave' module when
            using other core modules. Core modules should not be sold
            independently of the 'HR Essentials & Leave' module. Do you want to
            continue anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHrWarningDialog(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowHrWarningDialog(false);
              if (pendingAction === 'generate') {
                handleGenerateQuote();
              } else if (pendingAction === 'finalize') {
                handleFinaliseQuote();
              }
              setPendingAction(null);
            }}
            color="primary"
            autoFocus
          >
            Continue Anyway
          </Button>
        </DialogActions>
      </Dialog>

      <QuoteGenerationDialog
        open={showGenerationDialog}
        quoteData={quoteDataForGeneration}
        companyName={quoteDataForGeneration?.companyName || companyName}
        onClose={handleCloseGenerationDialog}
        onGenerationSuccess={handleGenerationSuccess}
        proposalsFolderId={selectedCompanyFromDropdown?.proposals_folder_id}
        selectedCompany={selectedCompanyFromDropdown!} // Assert non-null as dialog is shown after company selection
      />

      <PricingTableDialog
        open={pricingDialogOpen}
        onClose={() => setPricingDialogOpen(false)}
      />

      {/* Company Association Dialog */}
      <Dialog
        open={associationDialogOpen}
        onClose={handleCloseAssociationDialog}
        aria-labelledby="company-association-dialog-title"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle id="company-association-dialog-title">
          Link Proposal to Company
        </DialogTitle>
        <DialogContent>
          {updateSuccess ? (
            <Alert severity="success" variant="filled">
              Company updated successfully!
            </Alert>
          ) : (
            <>
              <Typography gutterBottom>
                This proposal{' '}
                <strong>"{selectedQuoteForAssociation?.companyName}"</strong> is
                not properly linked to a company in the system. This means it
                won't appear in the Product Stack section of a company. You can
                link it to an existing company below.
              </Typography>

              {updateError && (
                <Alert
                  severity="error"
                  style={{ marginTop: '16px', marginBottom: '16px' }}
                >
                  {updateError}
                </Alert>
              )}

              <Box mt={3} mb={3}>
                <Autocomplete
                  getOptionLabel={(c) => c.company_title}
                  options={companies ?? []}
                  loading={loadingCompanies}
                  value={selectedCompanyForAssociation}
                  onChange={(e, newValue) => {
                    setSelectedCompanyForAssociation(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select Company"
                      fullWidth
                      placeholder="Start typing to search for a company"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!updateSuccess && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateCompany}
              disabled={!selectedCompanyForAssociation || updatingCompany}
              startIcon={
                updatingCompany ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {updatingCompany ? 'Updating...' : 'Link Company'}
            </Button>
          )}
          <Button onClick={handleCloseAssociationDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccessSnackbar(false)} severity="success">
          Proposal generated successfully!
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default QuoteGeneratorView;
